import { useEffect, useState, useMemo, useCallback } from 'react'
import { useBottomSheet } from '../../state-management/contexts/BottomSheetContext'
import { ExecutionActionsContent } from '../../components/BottomSheetComponents/mf-execution/ExecutionActionsContent'
import {
  ExecutionBarConfig,
  ExecutionLearnMoreReason,
  MFExecutionData,
  PaymentPageType,
  RegularPlanSheetVariant,
  UserInvestmentStatus
} from '../../types/mf-execution.types'
import { useActionButtons } from './useActionButtons'
import { useSubActionButtons } from './useSubActionButtons'
import { ExecutionLearnMoreContent } from '../../components/BottomSheetComponents/mf-execution/ExecutionLearnMoreContent'
import { ExecutionRegularPlanLearnMoreContent } from '../../components/BottomSheetComponents/mf-execution/ExecutionRegularPlanLearnMoreContent'
import { NotRecommendedFund } from '../../components/BottomSheetComponents/mf-execution/NotRecommendedFund'
import { InfinitePortfolioInvestBottomSheet } from '../../components/BottomSheetComponents/mf-execution/InfinitePortfolioInvestBottomSheet'
import { useMetaDataContext } from '../../state-management/contexts/MetaDataContext'
import { getExecutionLearnMoreConfig } from '../../utils/mf-execution/learnMoreConfig'
import { useNavigate } from 'react-router-dom'
import { TransferLearnMoreContent } from '../../components/BottomSheetComponents/mf-execution/TransferLearnMoreContent'
import { WithdrawalLearnMoreContent } from '../../components/BottomSheetComponents/mf-execution/WithdrawalLearnMoreContent'

export const useMFExecutionUI = (data: MFExecutionData) => {
  const navigate = useNavigate()
  const { isin } = useMetaDataContext()
  const [currentAction, setCurrentAction] = useState<'invest' | 'withdraw' | 'more-options' | null>(null)
  const { openBottomSheet, openNestedBottomSheet, closeBottomSheet, closeNestedBottomSheet, updateMainBottomSheet } =
    useBottomSheet()

  const TOP_Z_INDEX = 30000
  const BOTTOM_Z_INDEX = 1

  const [isActionsSheetOpen, setIsActionsSheetOpen] = useState(false)
  const [isInfoSheetOpen, setIsInfoSheetOpen] = useState(false)
  const [isNotRecommendedSheetOpen, setIsNotRecommendedSheetOpen] = useState(false)
  const [isWithdrawalLearnMoreSheetOpen, setIsWithdrawalLearnMoreSheetOpen] = useState(false)
  const [isTransferLearnMoreSheetOpen, setIsTransferLearnMoreSheetOpen] = useState(false)
  const [executionBarZIndex, setExecutionBarZIndex] = useState(1)
  const [executionBarConfig, setExecutionBarConfig] = useState<ExecutionBarConfig>({
    buttons: []
  })

  const { data: metaData, directFundName } = useMetaDataContext()

  const directFundISIN = useMemo(() => {
    return metaData?.fundFamilyDirectFundIsin ?? ''
  }, [metaData])

  const fundName = metaData?.fundDetails?.fundName ?? ''

  const handlePaymentPageOpen = (paymentType: PaymentPageType) => {
    closeAllBottomSheets()
    setTimeout(() => {
      if (paymentType === PaymentPageType.WITHDRAW) {
        navigate(`/mutual-fund/${isin}/payment/${paymentType}/folio-selection`)
      } else {
        navigate(`/mutual-fund/${isin}/payment/${paymentType}`)
      }
    }, 100)
  }

  const openTransferLearnMoreSheet = useCallback(() => {
    setExecutionBarZIndex(BOTTOM_Z_INDEX)
    setIsTransferLearnMoreSheetOpen(true)
    openNestedBottomSheet(
      <TransferLearnMoreContent
        onDismiss={() => {
          closeTransferLearnMoreSheet()
        }}
        ScreenName='MF_L2'
        fundIsin={isin ?? ''}
        fundName={fundName}
        reason={data?.buttonDisabledStates?.transfer?.disabledReason}
      />,
      closeTransferLearnMoreSheet
    )
  }, [openNestedBottomSheet, data?.buttonDisabledStates?.transfer?.disabledReason, isin, fundName])

  const closeTransferLearnMoreSheet = useCallback(() => {
    setExecutionBarZIndex(TOP_Z_INDEX)
    setIsTransferLearnMoreSheetOpen(false)
    closeNestedBottomSheet()
  }, [closeNestedBottomSheet])

  const openInfoSheet = useCallback(
    (learnMoreReason: ExecutionLearnMoreReason) => {
      setExecutionBarZIndex(BOTTOM_Z_INDEX)
      setIsInfoSheetOpen(true)
      openNestedBottomSheet(
        <ExecutionLearnMoreContent
          onDismiss={() => {
            closeInfoSheet()
          }}
          ScreenName='MF_L2'
          reason={learnMoreReason}
          directFundISIN={directFundISIN}
          directFundName={directFundName}
          fundIsin={isin}
          fundName={fundName}
        />,
        closeInfoSheet
      )
    },
    [openNestedBottomSheet, directFundISIN, directFundName, isin, fundName]
  )

  const openRegularSheet = useCallback(
    (variant: RegularPlanSheetVariant) => {
      setExecutionBarZIndex(BOTTOM_Z_INDEX)
      setIsInfoSheetOpen(true)
      openNestedBottomSheet(
        <ExecutionRegularPlanLearnMoreContent
          onDismiss={() => {
            closeInfoSheet()
          }}
          ScreenName='MF_L2'
          variant={variant}
          directFundISIN={directFundISIN}
          directFundName={directFundName}
        />,
        closeInfoSheet
      )
    },
    [openNestedBottomSheet, directFundISIN, directFundName]
  )

  const { investButtons, withdrawButtons, moreOptionsButtons } = useSubActionButtons(
    data,
    handlePaymentPageOpen,
    openTransferLearnMoreSheet,
    openRegularSheet
  )

  const openActionsSheet = useCallback(
    (action: 'invest' | 'withdraw' | 'more-options') => {
      if (!isActionsSheetOpen) {
        setIsActionsSheetOpen(true)
        openBottomSheet(
          () => (
            <ExecutionActionsContent
              onDismiss={() => {
                closeActionsSheet()
              }}
              ScreenName='MF_L2'
              action={action}
              buttons={(() => {
                if (action === 'invest') {
                  return investButtons
                } else if (action === 'withdraw') {
                  return withdrawButtons
                } else {
                  return moreOptionsButtons
                }
              })()}
            />
          ),
          closeActionsSheet
        )
      }
    },
    [openBottomSheet, isActionsSheetOpen, currentAction, investButtons, withdrawButtons, moreOptionsButtons]
  )

  const closeActionsSheet = useCallback(() => {
    setIsActionsSheetOpen(false)
    closeBottomSheet()
    setCurrentAction(null)
  }, [closeBottomSheet])

  const closeInfoSheet = useCallback(() => {
    setIsInfoSheetOpen(false)
    closeNestedBottomSheet()
  }, [closeNestedBottomSheet])

  const openInfinitePortfolioInvestSheet = useCallback(() => {
    setExecutionBarZIndex(BOTTOM_Z_INDEX)
    openNestedBottomSheet(
      <InfinitePortfolioInvestBottomSheet
        onDismiss={() => {
          closeNestedBottomSheet()
        }}
        onContinue={() => {
          closeNestedBottomSheet()
          handleActionButtonClick('invest')
          openActionsSheet('invest')
        }}
      />,
      () => {
        closeNestedBottomSheet()
      }
    )
  }, [openNestedBottomSheet, closeNestedBottomSheet, openActionsSheet])

  const proceedToInvestActions = useCallback(() => {
    if (data?.isInfiniteUser) {
      openInfinitePortfolioInvestSheet()
      return
    }
    handleActionButtonClick('invest')
    openActionsSheet('invest')
  }, [data?.isInfiniteUser])

  const openNotRecommendedSheet = useCallback(
    (type: 'OFF_TRACK' | 'OUT_OF_FORM', actionType?: PaymentPageType.BUY | PaymentPageType.SIP) => {
      setExecutionBarZIndex(BOTTOM_Z_INDEX)
      setIsNotRecommendedSheetOpen(true)
      closeActionsSheet()
      openNestedBottomSheet(
        <NotRecommendedFund
          onDismiss={() => {
            closeNotRecommendedSheet()
          }}
          ScreenName='MF_L2'
          type={type}
          mfLogo={metaData?.fundLogo ?? ''}
          onInvest={() => {
            closeNotRecommendedSheet()
            if (data?.userInvestmentStatus === UserInvestmentStatus.INVESTED) {
              proceedToInvestActions()
            } else if (actionType) {
              handlePaymentPageOpen(actionType)
            }
          }}
          fundCategory={metaData?.fundDetails?.category ?? ''}
        />,
        closeNotRecommendedSheet
      )
    },
    [
      openNestedBottomSheet,
      metaData,
    ]
  )

  const closeNotRecommendedSheet = useCallback(() => {
    setIsNotRecommendedSheetOpen(false)
    closeNestedBottomSheet()
  }, [closeNestedBottomSheet])

  const openWithdrawalLearnMoreSheet = useCallback(() => {
    setExecutionBarZIndex(BOTTOM_Z_INDEX)
    setIsWithdrawalLearnMoreSheetOpen(true)
    closeActionsSheet()
    openNestedBottomSheet(
      <WithdrawalLearnMoreContent
        onDismiss={() => {
          closeWithdrawalLearnMoreSheet()
        }}
        ScreenName='MF_L2'
        fundIsin={isin ?? ''}
        fundName={fundName}
        reason={data?.buttonDisabledStates?.withdrawButton?.disabledReason}
      />,
      closeWithdrawalLearnMoreSheet
    )
  }, [openNestedBottomSheet, data?.buttonDisabledStates?.withdrawButton?.disabledReason, isin, fundName])

  const closeWithdrawalLearnMoreSheet = useCallback(() => {
    setIsWithdrawalLearnMoreSheetOpen(false)
    closeNestedBottomSheet()
  }, [closeNestedBottomSheet])

  const closeAllBottomSheets = useCallback(() => {
    closeActionsSheet()
    closeInfoSheet()
    closeNotRecommendedSheet()
    closeWithdrawalLearnMoreSheet()
  }, [closeActionsSheet, closeInfoSheet, closeNotRecommendedSheet, closeWithdrawalLearnMoreSheet])

  useEffect(() => {
    if (isActionsSheetOpen && !isInfoSheetOpen) {
      setExecutionBarZIndex(TOP_Z_INDEX)
    } else if (!isActionsSheetOpen && !isInfoSheetOpen) {
      setTimeout(() => {
        setExecutionBarZIndex(BOTTOM_Z_INDEX)
      }, 300)
    } else {
      setExecutionBarZIndex(BOTTOM_Z_INDEX)
    }
  }, [isActionsSheetOpen, isInfoSheetOpen])

  const handleActionButtonClick = (action: 'invest' | 'withdraw' | 'more-options') => {
    setCurrentAction(action)

    // Update the bottom sheet content if it's open
    if (isActionsSheetOpen) {
      if (action === currentAction) {
        closeActionsSheet()
        return
      }
      updateMainBottomSheet(() => (
        <ExecutionActionsContent
          onDismiss={() => { }}
          ScreenName='MF_L2'
          action={action}
          buttons={(() => {
            if (action === 'invest') {
              return investButtons
            } else if (action === 'withdraw') {
              return withdrawButtons
            } else {
              return moreOptionsButtons
            }
          })()}
        />
      ))
    }
  }

  const { investmentExistsButtons, investmentDoesNotExistButtons } = useActionButtons(
    openActionsSheet,
    openNotRecommendedSheet,
    openWithdrawalLearnMoreSheet,
    handleActionButtonClick,
    handlePaymentPageOpen,
    openRegularSheet,
    proceedToInvestActions,
    currentAction,
    data
  )

  const buildExecutionBarConfig = useMemo(() => {
    let config: ExecutionBarConfig = {
      buttons:
        data?.userInvestmentStatus === UserInvestmentStatus.INVESTED
          ? investmentExistsButtons
          : investmentDoesNotExistButtons
    }
    if (!isActionsSheetOpen && data?.buttonDisabledStates?.learnMoreReason) {
      config.learnMore = {
        text:
          getExecutionLearnMoreConfig(data?.buttonDisabledStates?.learnMoreReason)?.heading ?? 'Blocked, Learn More',
        onClick: () => {
          openInfoSheet(data?.buttonDisabledStates?.learnMoreReason ?? ExecutionLearnMoreReason.GENERIC)
        }
      }
    }
    return config
  }, [data?.userInvestmentStatus, investmentExistsButtons, investmentDoesNotExistButtons, isActionsSheetOpen])

  useEffect(() => {
    setExecutionBarConfig(buildExecutionBarConfig)
  }, [buildExecutionBarConfig])

  return {
    isActionsSheetOpen,
    isInfoSheetOpen,
    executionBarZIndex,
    executionBarConfig
  }
}
